import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Pencil, XCircle } from "lucide-react";

export function ModificaRichiestaDialog({ token, request, onClose, onSaved }) {
  const usaOrari = request.tipo === "permesso" || request.ore_calcolate != null;
  const [form, setForm] = useState({
    data_inizio: request.data_inizio, data_fine: request.data_fine,
    ora_inizio: request.ora_inizio || "", ora_fine: request.ora_fine || "", nota: "",
  });
  const [saving, setSaving] = useState(false);
  const [cancelMode, setCancelMode] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const annulla = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/leave-requests/${request.id}/revoca`, { nota: form.nota.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Richiesta annullata. Giorni/ore ripristinati e dipendente avvisato.");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nell'annullamento"); }
    finally { setSaving(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/leave-requests/${request.id}/modifica`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Richiesta modificata. Il dipendente è stato avvisato.");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nella modifica"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="modifica-richiesta-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5 text-blue-600" /> Modifica richiesta approvata</DialogTitle>
          <DialogDescription>
            {request.tipo === "ferie" ? "Ferie" : "Permesso"} di {request.user_nome} {request.user_cognome}. La richiesta resta approvata con le nuove date; il dipendente riceve una notifica e i saldi si aggiornano da soli.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Dal</Label><Input type="date" value={form.data_inizio} onChange={set("data_inizio")} data-testid="modifica-data-inizio" /></div>
          <div><Label>Al</Label><Input type="date" value={form.data_fine} onChange={set("data_fine")} data-testid="modifica-data-fine" /></div>
          {usaOrari && (
            <>
              <div><Label>Dalle ore{request.ora_inizio ? "" : " (facoltativo)"}</Label><Input type="time" value={form.ora_inizio} onChange={set("ora_inizio")} data-testid="modifica-ora-inizio" /></div>
              <div><Label>Alle ore{request.ora_inizio ? "" : " (facoltativo)"}</Label><Input type="time" value={form.ora_fine} onChange={set("ora_fine")} data-testid="modifica-ora-fine" /></div>
            </>
          )}
          <div className="col-span-2">
            <Label>Nota per il dipendente (facoltativa)</Label>
            <Textarea value={form.nota} onChange={set("nota")} placeholder="Es. spostata su richiesta della produzione" data-testid="modifica-nota" />
          </div>
        </div>
        {cancelMode ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-3" data-testid="annulla-conferma-box">
            <p className="text-sm text-red-800">
              Annullare del tutto questa richiesta approvata? Il dipendente riceverà una notifica e i giorni/ore torneranno nel suo saldo. La nota qui sopra, se scritta, verrà inviata al dipendente.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setCancelMode(false)} disabled={saving}>No, torna indietro</Button>
              <Button onClick={annulla} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white" data-testid="annulla-conferma-btn">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Sì, annulla la richiesta
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row sm:justify-between gap-2 pt-2">
            <Button variant="outline" onClick={() => setCancelMode(true)} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="annulla-richiesta-btn">
              <XCircle className="h-4 w-4 mr-1" /> Annulla richiesta
            </Button>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose}>Chiudi</Button>
              <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="modifica-salva-btn">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Salva modifica
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
